/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import {
	CardAction,
	ElementName,
	SmartLinkSize,
	TitleBlock,
	type CardProps,
} from '@atlaskit/smart-card';
import { CardSSR as Card, type CardSSRProps } from '@atlaskit/smart-card/ssr';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { RemoveButton } from './remove-button/index.tsx';

const uiOptions: CardProps['ui'] = {
	clickableContainer: true,
	hideBackground: true,
	hideElevation: true,
	hidePadding: true,
	size: SmartLinkSize.Medium,
};

const uiOptionsWithPlaceholder: CardProps['ui'] = {
	...uiOptions,
	removeBlockRestriction: true,
};

const actionOptions = {
	hide: false,
	exclude: [CardAction.ChangeStatusAction],
};

type TitleBlockProps = Parameters<typeof TitleBlock>[0];
type Props = Pick<CardSSRProps, 'url' | 'showHoverPreview' | 'onError' | 'placeholderData'> &
	Pick<TitleBlockProps, 'anchorTarget' | 'text' | 'anchorRef'> & {
		/** When defined this will display a remove button after the link that calls `onRemove` when clicked. */
		onRemove?: () => void;
	};

export const SimpleInlineSmartLink = ({
	onRemove,
	url,
	anchorTarget,
	showHoverPreview = true,
	text,
	anchorRef,
	onError,
	placeholderData,
}: Props): JSX.Element => {
	return (
		<Box paddingBlock="space.025" paddingInline="space.050" xcss={inlineStyles}>
			<Card
				appearance="inline"
				url={url}
				ui={placeholderData && fg('goals_ssr_smart_card') ? uiOptionsWithPlaceholder : uiOptions}
				actionOptions={actionOptions}
				showHoverPreview={showHoverPreview}
				placeholderData={placeholderData}
				onError={onError}
			>
				<TitleBlock
					hideTitleTooltip
					maxLines={1}
					metadata={[
						{
							name: ElementName.State,
						},
					]}
					anchorTarget={anchorTarget ?? '_self'}
					text={fg('goals_ssr_smart_card') ? undefined : text}
					css={titleBlockStyles}
					anchorRef={anchorRef}
					{...(fg('goals_ssr_smart_card') ? { hideIconLoadingSkeleton: !!placeholderData } : {})}
				/>
			</Card>
			{onRemove && <RemoveButton onClick={onRemove} />}
		</Box>
	);
};

const inlineStyles = xcss({
	backgroundColor: 'elevation.surface.raised',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderRadius: 'radius.small',
	display: 'inline-flex',
	gap: 'space.050',
});

const titleBlockStyles = css({
	gap: token('space.050'),
});
